import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import WorkoutCard from "@/components/WorkoutCard";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const targetId = userId || me?.id;
  const [profile, setProfile] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetId) return;
    setLoading(true);
    (async () => {
      try {
        const [{ data: p }, { data: w }] = await Promise.all([
          api.get(`/users/${targetId}`),
          api.get(`/workouts/user/${targetId}`),
        ]);
        setProfile(p);
        setWorkouts(w);
      } finally {
        setLoading(false);
      }
    })();
  }, [targetId]);

  const toggleFollow = async () => {
    if (!profile) return;
    if (profile.is_following) {
      await api.delete(`/users/${profile.id}/follow`);
      setProfile((p) => ({ ...p, is_following: false, followers: Math.max(0, p.followers - 1) }));
    } else {
      await api.post(`/users/${profile.id}/follow`);
      setProfile((p) => ({ ...p, is_following: true, followers: p.followers + 1 }));
    }
  };

  if (loading) return <div className="text-zinc-500 font-mono text-sm">Loading…</div>;
  if (!profile) return <div className="text-zinc-500">User not found</div>;

  const isMe = me?.id === profile.id;

  return (
    <div className="space-y-8" data-testid="profile-page">
      <div className="border border-[#27272A] bg-[#121212] p-6 md:p-8">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-[#27272A] flex items-center justify-center flex-shrink-0">
            <span className="font-display font-black text-white text-4xl">
              {(profile.name || "?").charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-mono">@{profile.username}</div>
            <h1 className="font-display text-4xl md:text-5xl font-black uppercase truncate">{profile.name}</h1>
            {profile.bio && <p className="text-zinc-400 mt-1 text-sm">{profile.bio}</p>}
            {!isMe && (
              <button
                data-testid="follow-button"
                onClick={toggleFollow}
                className={profile.is_following ? "btn-ghost mt-4" : "btn-primary mt-4"}
              >
                {profile.is_following ? "Following" : "Follow"}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 border border-[#27272A] mt-6">
          <div className="p-4 border-r border-[#27272A]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Workouts</div>
            <div className="font-display text-3xl font-black text-white">{profile.workout_count}</div>
          </div>
          <div className="p-4 border-r border-[#27272A]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Volume</div>
            <div className="font-display text-3xl font-black text-white">
              {Math.round(profile.total_volume).toLocaleString()}
              <span className="text-xs text-zinc-500 font-mono ml-1">kg</span>
            </div>
          </div>
          <div className="p-4 border-r border-[#27272A]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Followers</div>
            <div className="font-display text-3xl font-black text-white">{profile.followers}</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Following</div>
            <div className="font-display text-3xl font-black text-white">{profile.following}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-mono mb-2">// Sessions</div>
        <h2 className="font-display text-3xl font-black uppercase mb-4">Workout history</h2>
        {workouts.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-8 text-center text-zinc-500 text-sm">
            No workouts yet.
          </div>
        ) : (
          <div className="space-y-6">
            {workouts.map((w) => <WorkoutCard key={w.id} workout={w} />)}
          </div>
        )}
      </div>
    </div>
  );
}
